'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: string;
  href: string;
  color: 'primary' | 'secondary' | 'accent' | 'success';
}

interface QuickActionsProps {
  actions: QuickAction[];
  onCreateTaskClick?: () => void;
}

const QuickActions = ({ actions, onCreateTaskClick }: QuickActionsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'secondary':
        return 'bg-secondary/10 text-secondary hover:bg-secondary/20';
      case 'accent':
        return 'bg-accent/10 text-accent hover:bg-accent/20';
      case 'success':
        return 'bg-success/10 text-success hover:bg-success/20';
      default:
        return 'bg-primary/10 text-primary hover:bg-primary/20';
    }
  };

  if (!isHydrated) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-24 bg-muted rounded-lg animate-pulse"
          ></div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {actions.map((action) => {
        const isCreateTask = action.id === 'action-4' && onCreateTaskClick;
        if (isCreateTask) {
          return (
            <button
              key={action.id}
              type="button"
              onClick={onCreateTaskClick}
              className={`p-4 rounded-lg transition-smooth hover:scale-[1.02] active:scale-[0.98] w-full text-left ${getColorClasses(
                action.color
              )}`}
            >
              <div className="flex items-start gap-3">
                <Icon name={action.icon as any} size={24} />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-caption font-semibold mb-1 text-[#1A1714]">
                    {action.label}
                  </h4>
                  <p className="text-xs font-caption text-[#1A1714]/80">
                    {action.description}
                  </p>
                </div>
                <Icon name="ChevronRightIcon" size={16} className="text-[#1A1714]/70" />
              </div>
            </button>
          );
        }
        return (
          <Link
            key={action.id}
            href={action.href}
            className={`p-4 rounded-lg transition-smooth hover:scale-[1.02] active:scale-[0.98] ${getColorClasses(
              action.color
            )}`}
          >
            <div className="flex items-start gap-3">
              <Icon name={action.icon as any} size={24} />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-caption font-semibold mb-1 text-[#1A1714]">
                  {action.label}
                </h4>
                <p className="text-xs font-caption text-[#1A1714]/80">
                  {action.description}
                </p>
              </div>
              <Icon name="ChevronRightIcon" size={16} className="text-[#1A1714]/70" />
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default QuickActions;